import { Reservations } from '@/global';
import {
  calculateBalance,
  calculateDeposit,
  calculateFee,
  calculateTotal,
  calculateValue,
  getAllVariants,
} from '@/lib/reservation';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

function escapeCsvField(value: string | number): string {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function useDownloadCsv() {
  const { t } = useTranslation();

  return useCallback(
    (data: Reservations[]) => {
      return mapDatatoCSV(data, t);
    },
    [t]
  );
}

function mapDatatoCSV(
  data: Reservations[],
  t: ReturnType<typeof useTranslation>['t']
) {
  const variants = getAllVariants(data);

  const columns = [
    t('reservation.columns.booked-date'),
    t('reservation.columns.booking-date'),
    t('reservation.columns.code'),
    t('reservation.columns.customer-name'),
    t('reservation.columns.phone'),
    t('reservation.columns.email'),
    t('reservation.columns.service'),
    t('reservation.columns.shift'),
    t('reservation.columns.status'),
    t('reservation.columns.total'),
    t('reservation.columns.fee'),
    t('reservation.columns.value'),
    t('reservation.columns.deposit'),
    t('reservation.columns.balance'),
    t('reservation.columns.payment-status'),
    t('reservation.columns.stripe'),
    ...variants.map((v) =>
      t(`reservation.columns.variant`, {
        variant: v.name,
        service: v.service.name,
      })
    ),
  ];

  const dataRows = data.map((r: Reservations) => {
    return [
      r.booked_date,
      r.booking_date,
      r.code,
      r.customer_name,
      r.customer_phone,
      r.customer_email,
      r.service.name,
      r.shift?.name ?? '',
      t(`common.reservation.${r.status}`),
      calculateTotal(r),
      calculateFee(r),
      calculateValue(r),
      calculateDeposit(r),
      calculateBalance(r),
      r.payment_status ? t(`reservation.payment.${r.payment_status}`) : '',
      r.payment_intent_id
        ? `https://dashboard.stripe.com/payments/${r.payment_intent_id}`
        : '',
      ...variants.map((v) => {
        const variant = r.variants.find((vr) => vr.variant.id === v.id);
        return variant?.amount ?? '0';
      }),
    ];
  });

  const headers = columns.map(escapeCsvField).join(',');
  const rows = dataRows.map((row) => row.map(escapeCsvField).join(',')).join('\n');

  const csvContent = '\uFEFF' + headers + '\n' + rows;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'reservations.csv';
  a.click();
}
